import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { getFullModulegetFullModuleHierarchyWithActionsHierarchy, updateRolePermission, insertRoleAndPermissions, deleteRoleAndPermissions } from '../../../services/adminService';
import { toast } from 'react-toastify';
import { usePermissions } from '../../../context/PermissionsContext';

function Permission() {
  const { permissions } = usePermissions();
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [activeModules, setActiveModules] = useState({
    parentId: null,
    childId: null,
    grandChildId: null
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [permissionChanges, setPermissionChanges] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editedRole, setEditedRole] = useState({ name: '', description: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newRole, setNewRole] = useState({
    name: '',
    description: ''
  });
  const [isCreating, setIsCreating] = useState(false);

  const getRoles = async () => {
    try {
      const response = await getFullModulegetFullModuleHierarchyWithActionsHierarchy();
      // console.log('roles response ----------------', response);
      if (response.success && response.data && response.data.length > 0) {
        setRoles(response.data);
        setSelectedRole(response.data[0]);
        setEditedRole({ name: response.data[0].name, description: response.data[0].description });
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  useEffect(() => {
    getRoles();
  }, []);

  const toggleModule = (moduleId, level, parentId = null) => {
    setActiveModules(prev => {
      switch (level) {
        case 'parent':
          if (prev.parentId === moduleId) {
            return { parentId: null, childId: null, grandChildId: null };
          }
          return { parentId: moduleId, childId: null, grandChildId: null };

        case 'child':
          if (parentId === prev.parentId) {
            if (prev.childId === moduleId) {
              return { ...prev, childId: null, grandChildId: null };
            }
            return { ...prev, childId: moduleId, grandChildId: null };
          }
          return prev;

        case 'grandChild':
          if (parentId === prev.childId) {
            return { ...prev, grandChildId: prev.grandChildId === moduleId ? null : moduleId };
          }
          return prev;

        default:
          return prev;
      }
    });
  };

  const handleRoleSelect = (role) => {
    if (!permissions.roles_and_access_permissions_view) {
      toast.warning("You don't have permission to view role details");
      return;
    }
    setSelectedRole(role);
    setActiveModules({ parentId: null, childId: null, grandChildId: null });
    setIsEditMode(false);
    setPermissionChanges([]);
    setEditedRole({ name: role.name, description: role.description });
  };

  const handleEditToggle = () => {
    if (!permissions.roles_and_access_permissions_edit) {
      toast.warning("You don't have permission to edit roles and permissions");
      return;
    }

    if (isEditMode) {
      // Save changes
      handleSavePermissions();
    } else {
      // Enable edit mode
      setIsEditMode(true);
      setPermissionChanges([]);
      setEditedRole({ name: selectedRole.name, description: selectedRole.description });
    }
  };

  const handleRoleFieldChange = (field, value) => {
    setEditedRole(prev => ({ ...prev, [field]: value }));
  };

  const handlePermissionChange = (actionId, actionSlug, currentValue) => {
    if (!isEditMode || !permissions.roles_and_access_permissions_edit) {
      if (!permissions.roles_and_access_permissions_edit) {
        toast.warning("You don't have permission to edit permissions");
      }
      return;
    }

    const newValue = currentValue === 1 ? 0 : 1;

    // Update the permission changes array
    setPermissionChanges(prev => {
      const existingIndex = prev.findIndex(change => change.permission_id === actionId);
      if (existingIndex !== -1) {
        // Update existing change
        const newChanges = [...prev];
        newChanges[existingIndex] = {
          role_id: selectedRole.id,
          permission_id: actionId,
          is_granted: newValue
        };
        return newChanges;
      } else {
        // Add new change
        return [...prev, {
          role_id: selectedRole.id,
          permission_id: actionId,
          is_granted: newValue
        }];
      }
    });

    // Update the UI immediately
    setSelectedRole(prev => {
      const updateActionsInModule = (module) => {
        const updatedModule = { ...module };

        // Update actions in current module
        if (updatedModule.actions) {
          updatedModule.actions = updatedModule.actions.map(action =>
            action.slug === actionSlug ? { ...action, is_granted: newValue } : action
          );
        }

        // Update actions in children
        if (updatedModule.children) {
          updatedModule.children = updatedModule.children.map(child => updateActionsInModule(child));
        }

        return updatedModule;
      };

      return {
        ...prev,
        permissions: prev.permissions.map(permission => updateActionsInModule(permission))
      };
    });
  };

  const handleSavePermissions = async () => {
    if (!permissions.roles_and_access_permissions_edit) {
      toast.warning("You don't have permission to edit roles and permissions");
      return;
    }

    setIsLoading(true);
    try {
      const updateData = {
        role_id: selectedRole.id,
        name: editedRole.name,
        description: editedRole.description,
        permissionsToUpdate: permissionChanges
      };

      // console.log('Updating permissions:', updateData);
      const response = await updateRolePermission(updateData);
      // console.log('response ----------------', response);

      if (response.success) {
        // Get fresh data after updating permissions
        const rolesResponse = await getFullModulegetFullModuleHierarchyWithActionsHierarchy();

        if (rolesResponse.success && rolesResponse.data) {
          // Find the current role in the fresh data
          const updatedRole = rolesResponse.data.find(role => role.id === selectedRole.id);

          if (updatedRole) {
            // Update roles state with complete data
            setRoles(rolesResponse.data);
            // Update selected role with fresh data
            setSelectedRole(updatedRole);
            setEditedRole({
              name: updatedRole.name,
              description: updatedRole.description
            });
          }
        }

        toast.success(response.message || 'Role and permissions updated successfully', {
          position: "top-center",
          autoClose: 3000
        });
        setIsEditMode(false);
        setPermissionChanges([]);
      } else {
        toast.error(response.message || 'Failed to update permissions', {
          position: "top-center",
          autoClose: 5000
        });
      }
    } catch (error) {
      toast.error('An error occurred while updating permissions', {
        position: "top-center",
        autoClose: 5000
      });
      console.error('Error updating permissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setPermissionChanges([]);
    setEditedRole({ name: selectedRole.name, description: selectedRole.description });
    // Refresh data to revert any UI changes
    getRoles();
  };

  const handleCreateRole = async () => {
    if (!permissions.roles_and_access_permissions_create) {
      toast.warning("You don't have permission to create new roles");
      return;
    }

    if (!newRole.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    setIsCreating(true);
    try {
      // Add 2 second delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      const response = await insertRoleAndPermissions({
        name: newRole.name.trim(),
        description: newRole.description.trim()
      });

      if (response.success) {
        // Get fresh data to ensure we have the complete role structure with permissions
        const rolesResponse = await getFullModulegetFullModuleHierarchyWithActionsHierarchy();

        if (rolesResponse.success && rolesResponse.data) {
          // Find the newly created role in the fresh data
          const newRoleData = rolesResponse.data.find(role => role.id === response.data.role_id);

          if (newRoleData) {
            // Update roles state with complete data
            setRoles(rolesResponse.data);

            // Select the newly created role
            setSelectedRole(newRoleData);
            setEditedRole({
              name: newRoleData.name,
              description: newRoleData.description
            });

            // Reset active modules when switching to new role
            setActiveModules({
              parentId: null,
              childId: null,
              grandChildId: null
            });
          }
        }

        toast.success(response.message || 'Role created successfully', {
          position: "top-center",
          autoClose: 3000
        });

        setShowCreateModal(false);
        setNewRole({ name: '', description: '' });
      } else {
        toast.error(response.message || 'Failed to create role');
      }
    } catch (error) {
      console.error('Error creating role:', error);
      toast.error(error.response.data.message || 'An error occurred while creating the role');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteClick = (role) => {

    setRoleToDelete(role);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;

    setIsDeleting(true);
    try {
      const response = await deleteRoleAndPermissions({ role_id: roleToDelete.id });

      if (response.success) {
        toast.success(response.message || 'Role deleted successfully');
        // Refresh roles list
        await getRoles();
        // If the deleted role was selected, select the first available role
        if (selectedRole?.id === roleToDelete.id) {
          const remainingRoles = roles.filter(role => role.id !== roleToDelete.id);
          if (remainingRoles.length > 0) {
            setSelectedRole(remainingRoles[0]);
            setEditedRole({
              name: remainingRoles[0].name,
              description: remainingRoles[0].description
            });
          } else {
            setSelectedRole(null);
            setEditedRole({ name: '', description: '' });
          }
        }
      } else {
        toast.error(response.response.data.message || 'Failed to delete role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error(error.response.data.message || 'An error occurred while deleting the role');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setRoleToDelete(null);
    }
  };

  const getDisplayName = (moduleName) => {
    const nameMapping = {
      'classroom': 'Live Session',
      'classrooms': 'Live Sessions',
      'Classroom': 'Live Session',
      'Classrooms': 'Live Sessions'
    };
    return nameMapping[moduleName] || moduleName;
  };

  const renderModulePermissions = (module, level = 'parent', parentId = null) => {
    const isActive = activeModules[level === 'parent' ? 'parentId' : level === 'child' ? 'childId' : 'grandChildId'] === module.id;

    // Count granted permissions
    const grantedPermissions = module.actions?.filter(action => action.is_granted === 1) || [];
    const totalPermissions = module.actions?.length || 0;

    // Get names of granted permissions
    const grantedPermissionNames = grantedPermissions.map(action => action.name).join(', ');

    return (
      <div key={module.id} className={`mb-3 transition-all duration-300 ${level === 'parent' ? 'bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden' : 'ml-6 mt-3 border-l-2 border-dashed border-gray-100 pl-4'}`}>
        <div
          className={`flex justify-between items-center p-4 cursor-pointer transition-all ${isActive ? 'bg-[#7083FF]/5' : 'hover:bg-gray-50'}`}
          onClick={() => toggleModule(module.id, level, parentId)}
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${isActive ? 'bg-[#7083FF] text-white' : 'bg-gray-50 text-gray-400 group-hover:bg-gray-100'}`}>
              <Icon icon={module.icon || (level === 'parent' ? "fluent:folder-24-regular" : "fluent:document-24-regular")} width="22" />
            </div>
            
            <div className="flex flex-col min-w-0">
              <span className={`font-bold transition-colors ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>{getDisplayName(module.name)}</span>
              {!isActive && grantedPermissions.length > 0 && (
                <span className="text-[10px] text-gray-400 font-medium truncate mt-0.5">
                  {grantedPermissionNames}
                </span>
              )}
            </div>
            
            {!isActive && grantedPermissions.length > 0 && (
                <div className="flex items-center ml-2 shrink-0">
                    <span className="px-2 py-0.5 rounded-lg bg-green-50 text-green-600 text-[10px] font-bold border border-green-100">
                        {grantedPermissions.length} Active
                    </span>
                </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {isActive && (
              <span className="text-[10px] font-bold text-[#7083FF] bg-[#7083FF]/10 px-2.5 py-1 rounded-lg">
                {grantedPermissions.length} / {totalPermissions} SET
              </span>
            )}
            <div className={`p-1.5 rounded-lg transition-all ${isActive ? 'bg-[#7083FF]/10 text-[#7083FF] rotate-180' : 'text-gray-400 hover:bg-gray-100'}`}>
                <Icon icon="fluent:chevron-down-24-regular" width="16" />
            </div>
          </div>
        </div>

        {isActive && (
          <div className="px-6 py-4 animate-in fade-in slide-in-from-top-2 duration-300">
            {module.actions && module.actions.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
                  {module.actions.map((action) => (
                    <div 
                        key={action.slug} 
                        className={`p-4 rounded-2xl border transition-all cursor-pointer group/perm ${action.is_granted === 1 ? 'bg-indigo-50/30 border-indigo-100' : 'bg-white border-gray-100 hover:border-gray-200'}`}
                        onClick={() => isEditMode && handlePermissionChange(action.id, action.slug, action.is_granted)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`relative w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${action.is_granted === 1 ? 'bg-indigo-600 text-white' : 'bg-gray-50 text-gray-400'}`}>
                            <Icon icon={action.is_granted === 1 ? "fluent:key-24-filled" : "fluent:key-24-regular"} width="18" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between mb-0.5">
                            <p className={`text-xs font-bold truncate ${action.is_granted === 1 ? 'text-indigo-900' : 'text-gray-700'}`}>
                                {action.action_name}
                            </p>
                            {isEditMode && (
                                <div className={`w-8 h-4 rounded-full relative transition-all ${action.is_granted === 1 ? 'bg-indigo-600' : 'bg-gray-200'}`}>
                                    <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${action.is_granted === 1 ? 'right-0.5' : 'left-0.5 shadow-sm'}`} />
                                </div>
                            )}
                          </div>
                          <p className="text-[10px] text-gray-400 leading-relaxed font-medium">
                            {action.description || 'No description provided'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
            )}
            
            <div className="space-y-1">
                {module.children?.map((childModule) =>
                  renderModulePermissions(
                    childModule,
                    level === 'parent' ? 'child' : level === 'child' ? 'grandChild' : 'grandChild',
                    module.id
                  )
                )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Modals with Backdrop Blur */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-[32px] shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
            <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#7083FF] flex items-center justify-center text-white shadow-xl shadow-[#7083FF]/20">
                      <Icon icon="fluent:person-key-24-regular" width={24} />
                  </div>
                  <h5 className="font-bold text-xl text-gray-900">Create New Role</h5>
              </div>
              <button type="button" className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all font-bold" onClick={() => setShowCreateModal(false)}>
                <Icon icon="fluent:dismiss-24-regular" width={22} />
              </button>
            </div>
            
            <div className="p-8 space-y-6">
                <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100 flex items-start gap-3">
                    <Icon icon="fluent:info-24-regular" className="text-blue-500 mt-0.5 shrink-0" width={18} />
                    <p className="text-xs text-blue-700 leading-relaxed font-medium">
                        Define a new role and its purpose. You will be able to configure specific module permissions after the role is created.
                    </p>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2.5">Role Name</label>
                    <input
                      type="text"
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#7083FF]/10 focus:border-[#7083FF]/30 transition-all text-sm font-bold placeholder:text-gray-300"
                      value={newRole.name}
                      onChange={(e) => setNewRole(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Content Manager, Super Admin"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2.5">Description (Optional)</label>
                    <textarea
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#7083FF]/10 focus:border-[#7083FF]/30 transition-all text-sm font-medium placeholder:text-gray-300 whitespace-pre-wrap resize-none"
                      rows="3"
                      value={newRole.description}
                      onChange={(e) => setNewRole(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="What responsibilities does this role have?"
                    />
                </div>
            </div>

            <div className="px-8 py-6 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
              <button
                className="flex-1 px-6 py-3.5 rounded-2xl border border-gray-200 text-gray-700 text-sm font-bold hover:bg-white transition-all disabled:opacity-50"
                onClick={() => setShowCreateModal(false)}
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-6 py-3.5 rounded-2xl bg-[#7083FF] text-white text-sm font-bold shadow-xl shadow-[#7083FF]/20 hover:bg-[#5f73e6] transition-all flex items-center justify-center gap-2"
                onClick={handleCreateRole}
                disabled={isCreating}
              >
                {isCreating ? <Icon icon="fluent:spinner-ios-20-regular" className="animate-spin" width={20} /> : "Create Role"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white rounded-[32px] shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
            <div className="p-8 text-center">
                <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center mx-auto mb-6 rotate-3">
                    <Icon icon="fluent:delete-dismiss-24-regular" className="text-red-500" width={40} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Delete Role?</h3>
                <p className="text-gray-500 text-sm leading-relaxed px-4">
                  Are you absolutely sure you want to delete <span className="text-red-600 font-bold capitalize">&quot;{roleToDelete?.name}&quot;</span>? This action is permanent.
                </p>
            </div>
            <div className="px-8 py-6 bg-gray-50 flex flex-col sm:flex-row gap-3">
              <button
                className="flex-1 px-6 py-3 rounded-2xl border border-gray-200 text-gray-700 text-sm font-bold hover:bg-white transition-all"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                No, Keep it
              </button>
              <button
                className="flex-1 px-6 py-3 rounded-2xl bg-red-500 text-white text-sm font-bold shadow-xl shadow-red-100 hover:bg-red-600 transition-all flex items-center justify-center gap-2"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? <Icon icon="fluent:spinner-ios-20-regular" className="animate-spin" width={20} /> : 'Delete Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col xl:flex-row gap-8 items-start">
        {/* Roles List - Navigation Sidebar */}
        <div className="w-full xl:w-[340px] shrink-0 space-y-4">
          <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest px-2">Access Levels</h3>
              {permissions.roles_and_access_permissions_create && (
                <button 
                    onClick={() => setShowCreateModal(true)}
                    className="p-1.5 rounded-lg text-[#7083FF] hover:bg-[#7083FF]/10 transition-colors"
                >
                    <Icon icon="fluent:add-circle-24-regular" width={22} />
                </button>
              )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-3">
            {roles.map((role) => (
              <button
                key={role.id}
                className={`group relative text-left p-4 rounded-3xl border transition-all duration-300 ${
                    selectedRole?.id === role.id 
                    ? 'bg-white border-[#7083FF] shadow-xl shadow-[#7083FF]/10 ring-1 ring-[#7083FF]' 
                    : 'bg-white border-gray-100 hover:border-gray-300 hover:shadow-md'
                }`}
                onClick={() => handleRoleSelect(role)}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                      selectedRole?.id === role.id ? 'bg-[#7083FF] text-white shadow-lg shadow-[#7083FF]/30' : 'bg-gray-50 text-gray-400 group-hover:bg-gray-100'
                  }`}>
                    <Icon icon={role.name?.toLowerCase() === 'admin' ? "fluent:badge-24-regular" : "fluent:person-board-24-regular"} width={24} />
                  </div>
                  <div className="min-w-0 pr-4">
                    <p className={`font-bold capitalize truncate leading-tight transition-colors ${selectedRole?.id === role.id ? 'text-gray-900' : 'text-gray-700'}`}>
                      {role.name}
                    </p>
                    <p className="text-[11px] text-gray-400 font-medium truncate mt-1">
                      {role.description || 'No description'}
                    </p>
                  </div>
                </div>
                {selectedRole?.id === role.id && (
                    <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-8 bg-[#7083FF] rounded-l-full" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Permissions Management - Detailed Content Area */}
        <div className="flex-1 w-full min-w-0 space-y-6">
          {selectedRole ? (
            <div className="bg-white rounded-[40px] border border-gray-100 shadow-sm overflow-hidden min-h-[600px] flex flex-col">
              {/* Header section with Role Info and Actions */}
              <div className="px-8 py-8 border-b border-gray-50 bg-gradient-to-r from-gray-50/50 to-white flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                <div className="flex-1 min-w-0 space-y-2">
                    {isEditMode ? (
                      <div className="space-y-4 max-w-2xl">
                        <div className="group/input">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 mb-1.5 block">Update Identity</label>
                            <input
                                type="text"
                                className="w-full px-6 py-4 bg-white border border-gray-100 rounded-[20px] focus:outline-none focus:ring-4 focus:ring-[#7083FF]/10 focus:border-[#7083FF]/30 transition-all text-xl font-bold placeholder:text-gray-200"
                                value={editedRole.name}
                                onChange={(e) => handleRoleFieldChange('name', e.target.value)}
                                placeholder="Group Name"
                            />
                        </div>
                        <div className="group/input">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 mb-1.5 block">Role Context</label>
                            <textarea
                                className="w-full px-6 py-4 bg-white border border-gray-100 rounded-[20px] focus:outline-none focus:ring-4 focus:ring-[#7083FF]/10 focus:border-[#7083FF]/30 transition-all text-sm font-medium placeholder:text-gray-200 resize-none overflow-hidden h-20"
                                value={editedRole.description}
                                onChange={(e) => handleRoleFieldChange('description', e.target.value)}
                                placeholder="Describe what people with this role can do..."
                            />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                            <h2 className="text-3xl font-black text-gray-900 tracking-tight capitalize">{selectedRole.name}</h2>
                            <div className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 text-[10px] font-black uppercase tracking-widest border border-blue-100">
                                {selectedRole.name?.toLowerCase() === 'admin' ? 'Root Access' : 'Custom Policy'}
                            </div>
                        </div>
                        <p className="text-gray-500 font-medium leading-relaxed max-w-2xl">
                          {selectedRole.description || 'Define specific module permissions to control what users in this group can see and perform within the administrator panel.'}
                        </p>
                      </>
                    )}
                </div>

                <div className="flex items-center gap-3 shrink-0 self-start sm:self-center">
                  {selectedRole?.name?.toLowerCase() !== 'admin' && !isEditMode && (
                    <button
                      className="w-12 h-12 rounded-2xl bg-white border border-gray-100 text-gray-400 hover:text-red-500 hover:border-red-100 hover:shadow-xl hover:shadow-red-500/5 transition-all flex items-center justify-center group/del"
                      onClick={() => handleDeleteClick(selectedRole)}
                      disabled={isLoading || !permissions.roles_and_access_permissions_delete}
                      title="Destroy Role"
                    >
                      <Icon icon="fluent:delete-24-regular" width={22} />
                    </button>
                  )}
                  
                  {isEditMode && (
                    <button
                      className="px-6 py-3.5 rounded-2xl bg-white border border-gray-200 text-gray-700 text-sm font-bold hover:shadow-md transition-all flex items-center gap-2"
                      onClick={handleCancelEdit}
                      disabled={isLoading}
                    >
                      <Icon icon="fluent:dismiss-24-regular" width={18} />
                      Discard
                    </button>
                  )}

                  {permissions.roles_and_access_permissions_edit && (
                    <button
                      className={`px-8 py-3.5 rounded-2xl text-white text-sm font-bold shadow-xl transition-all flex items-center gap-3 hover:scale-[1.02] active:scale-[0.98] ${
                          isEditMode ? 'bg-indigo-600 shadow-indigo-200' : 'bg-[#7083FF] shadow-[#7083FF]/20 hover:bg-[#5f73e6]'
                      }`}
                      onClick={handleEditToggle}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Icon icon="fluent:spinner-ios-20-regular" className="animate-spin" width={20} />
                      ) : (
                        <>
                          <Icon icon={isEditMode ? "fluent:save-24-filled" : "fluent:edit-compose-24-regular"} width={20} />
                          {isEditMode ? "Deploy Changes" : "Modify Policy"}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Permissions Tree Area */}
              <div className="flex-1 overflow-y-auto bg-gray-50/20 p-8">
                {isEditMode && (
                  <div className="mb-8 p-5 bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-3xl text-white shadow-xl shadow-indigo-100 flex items-center gap-5">
                      <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center shrink-0">
                          <Icon icon="fluent:flash-24-filled" width={24} />
                      </div>
                      <div>
                          <h4 className="font-bold text-lg leading-tight text-white/100">Draft Mode Active</h4>
                          <p className="text-xs text-white/70 font-medium">Permission changes are local. Be sure to Deploy Changes to sync to the server.</p>
                      </div>
                  </div>
                )}
                
                <div className="space-y-4">
                  {selectedRole.permissions?.length > 0 ? (
                      selectedRole.permissions.map((module) => renderModulePermissions(module))
                  ) : (
                    <div className="py-40 text-center flex flex-col items-center">
                        <div className="w-24 h-24 bg-gray-50 rounded-[40px] flex items-center justify-center mb-6">
                            <Icon icon="fluent:document-error-24-regular" className="text-gray-200" width={48} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-400">No permissions mapped</h3>
                        <p className="text-gray-400 text-sm mt-2 font-medium max-w-xs">This role doesn&apos;t have any modules visibility. Contact system engineer if this is unexpected.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-[600px] bg-white rounded-[40px] border border-gray-100 shadow-sm flex flex-col items-center justify-center p-12 text-center animate-in fade-in duration-700">
                <div className="w-32 h-32 bg-[#7083FF]/5 rounded-[48px] flex items-center justify-center mb-8">
                    <Icon icon="fluent:fingerprint-24-regular" className="text-[#7083FF]" width={64} />
                </div>
                <h2 className="text-2xl font-black text-gray-900 tracking-tight mb-3">Identity Access Management</h2>
                <p className="text-gray-500 font-medium leading-relaxed max-w-md">
                    Select a security role from the left pane to view, edit, or remove specific platform permissions and administrative policies.
                </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Permission;
